import { useEffect, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { getSessionResult, isAdult1822 } from "@/lib/result.functions";
import type { GetSessionResultResponse } from "@/lib/result.functions";
import { trackReportDelivered, oncePerSession } from "@/lib/analytics";
import { getReportPdfDownloadUrl } from "@/lib/download-report-pdf.functions";
import { useT } from "@/lib/i18n";
import { useSessionLanguage } from "@/lib/use-session-language";
import { offerHref } from "@/lib/report-offers";
import { ReportOffersMobile } from "@/components/report/ReportOffer";
import { Report10Page } from "@/components/report/Report10Page";
import { Report11Page } from "@/components/report/Report11Page";
import { Report12Page } from "@/components/report/Report12Page";
import { Report1011Page } from "@/components/report/Report1011Page";
import Report13Page from "@/components/report/Report13Page";
import { ConsultationCTA } from "@/components/ConsultationCTA";
import { UI as CareersUI } from "@/careers/spheres";
import { normAccountLocale, type AccountLocale } from "@/lib/locale";
import { usePublishPageLanguage } from "@/lib/page-language";

// Every iOS browser is a WebKit re-wrap (Apple's App Store rules),
// and WebKit silently ignores <a download> on cross-origin URLs
// regardless of which shell is around it — Safari, Chrome-for-iOS
// (CriOS), Edge-for-iOS (EdgiOS), Firefox-for-iOS (FxiOS) all behave
// the same way on the download-attribute path. The earlier isIosSafari
// gate routed only Safari through window.open; Edge iOS took the
// broken <a download> branch and produced a silent no-op. Treating
// every iOS browser as WebKit here funnels all of them through
// window.open, which opens the PDF in Safari's native viewer + Share
// Sheet ("Save to Files").
function isIosWebKit(): boolean {
  return /iP(hone|ad|od)/.test(navigator.userAgent);
}

// Non-iOS-only. Desktop / Android / non-WebKit routes go through the
// hidden anchor + click. The iOS path lives inline in handleDownload
// because it needs to open the target window SYNCHRONOUSLY inside the
// click handler — Safari drops user activation across await, so calling
// window.open() after `await fetchPdfUrl(...)` is silently blocked as
// a popup. See handleDownload for the iOS path.
function triggerDesktopDownload(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Client-side timeout wrapper. TanStack Start's server-fn call
// (via useServerFn) was observed hanging forever on iOS WebKit —
// neither resolving nor rejecting — leaving the UI stuck in the
// "loading" state with no visible error. Full root-cause diagnosis
// needs a Mac + iPhone + Safari Web Inspector, which we do not have;
// this timeout is a symptomatic defence so the UI always exits the
// loading state. 45s covers the cache-hit case (near-instant) and
// most on-demand puppeteer renders (10-60s). If a legitimate render
// exceeds 45s the user sees the timeout error and can retry.
const CLIENT_TIMEOUT_MS = 45_000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error("client timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (e) => {
        clearTimeout(id);
        reject(e);
      },
    );
  });
}

// Cabinet — build the snapshot payload for /api/cabinet/snapshot/save.
// Per-band identity: 18-22 (Result1822) & 14-15 → RIASEC; 10-11/12-13/16-18 →
// archetype. Pure mapping, no side effects; the caller fires it non-blocking.
function buildSnapshotPayload(data: GetSessionResultResponse, token: string) {
  if (isAdult1822(data)) {
    const r = data.result1822;
    const s = r.riasecScores;
    return {
      token,
      language: data.language_key,
      ageGroup: "18_22",
      riasec: { R: s.R, I: s.I, A: s.A, S: s.S, E: s.E, C: s.C },
      riasecCode: r.riasecCode,
      topValues: r.topValues,
      topStyles: r.topStyles,
      topSkills: r.topSkills,
      clusters: r.topClusters.map((c) => ({ id: c.id, score: c.score })),
      confidence: r.confidence,
    };
  }
  const res = data.result;
  const rs = res.riasec_scores;
  const dom = res.dominant_riasec;
  return {
    token,
    language: data.language_key,
    ageGroup: res.age_group,
    testVersionId: res.test_version_id,
    reportTemplateVersionId: res.report_template_version_id,
    packageType: data.package_type,
    archetypePrimary: res.primary_archetype,
    archetypeSecondary: res.secondary_archetype,
    combinationKey: res.combination_key,
    riasec: rs ? { R: rs.R, I: rs.I, A: rs.A, S: rs.S, E: rs.E, C: rs.C } : null,
    riasecCode: dom ? (Array.isArray(dom) ? dom.join("") : dom) : null,
    scores: res.child_scores ?? null,
  };
}

export const Route = createFileRoute("/session/$token/result")({
  head: () => ({
    meta: [{ title: "Result — Yolutap" }, { name: "robots", content: "noindex" }],
  }),
  component: ResultPage,
});

function ResultPage() {
  const { token } = Route.useParams();
  // ?lang comes from the caller, and the caller is the only one who knows
  // whose screen this is: the cabinet passes the ACCOUNT's language (this is
  // the re-render — the report exists in all three languages and the scores
  // don't depend on any of them), while an anonymous post-test visit passes
  // nothing and falls through to the language the test was taken in. Declared
  // on the parent /session/$token route, inherited here.
  const { lang: requestedLang } = useSearch({ from: "/session/$token" });
  const fetchResult = useServerFn(getSessionResult);
  const fetchPdfUrl = useServerFn(getReportPdfDownloadUrl);

  const { data, isLoading, error } = useQuery({
    queryKey: ["session-result", token],
    queryFn: () => fetchResult({ data: { access_token: token } }),
  });

  // One language for the whole page — report, careers link, consultation CTA
  // and the chrome around them. Three sources disagreeing on one screen is the
  // defect being fixed here, not a detail.
  // Пока результат считается, язык берётся из лёгкого запроса сессии: иначе
  // экран загрузки на AZ и EN отчётах был по-русски (A4, 13.10).
  const sessionLang = useSessionLanguage(token);
  const lang: AccountLocale = requestedLang ?? normAccountLocale(data?.language_key ?? sessionLang);
  // Страница объявляет свой язык, атрибут ставит RootShell. Без этого
  // браузер переводит регистр по русским правилам и печатает «TARIX» вместо
  // «TARİX» — семьдесят семь заголовков, найдено живым прогоном 7 сентября.
  usePublishPageLanguage(lang);
  const t = useT(lang);

  const [dlState, setDlState] = useState<"idle" | "loading" | "error">("idle");
  const [dlError, setDlError] = useState<string | null>(null);

  // Marketing `report_delivered` — fired once per browser when the finished
  // report is first rendered for the user. Gated on status "complete": an
  // "in_progress" result means scoring isn't done, so it's not delivered yet.
  // Deduped locally by token; no identifier is sent to the vendors.
  useEffect(() => {
    if (!data || data.status !== "complete") return;
    if (!oncePerSession(`report_delivered_${token}`)) return;
    const ageGroup = isAdult1822(data) ? "18_22" : (data.result?.age_group ?? "unknown");
    trackReportDelivered({ ageGroup, language: data.language_key });
  }, [data, token]);

  // Cabinet — persist the computed result as a snapshot (fire-and-forget).
  // STRICTLY non-blocking: any failure here must never affect the report. Gated
  // on status "complete" and deduped once per browser session (the endpoint is
  // also idempotent per session). No identifier is surfaced to the user.
  useEffect(() => {
    if (!data || data.status !== "complete") return;
    if (!oncePerSession(`cabinet_snapshot_${token}`)) return;
    let payload: ReturnType<typeof buildSnapshotPayload>;
    try {
      payload = buildSnapshotPayload(data, token);
    } catch (e) {
      console.debug("[cabinet-snapshot] build skipped:", e);
      return;
    }
    void fetch("/api/cabinet/snapshot/save", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((e) => {
      // Best-effort only — never surface to the report.
      console.debug("[cabinet-snapshot] save skipped:", e);
    });
  }, [data, token]);

  async function handleDownload() {
    if (dlState === "loading") return; // in-flight click guard on the UI

    // iOS Safari (all WebKit-on-iOS) drops user activation across
    // await. If we called window.open() AFTER `await fetchPdfUrl(...)`
    // Safari would silently block it as a popup, `triggerDownload`
    // would no-op, `setDlState("idle")` would run normally, and the
    // user would see the button return from "Fayl hazırlanır…" to
    // "PDF yüklə" with no file and no error — the exact reported
    // symptom. Fix: open a blank tab SYNCHRONOUSLY here inside the
    // click's activation window, then navigate it to the signed URL
    // after fetch resolves.
    //
    // No `noopener` on the open() call because that would force the
    // return value to null (per WHATWG); we detach `opener` manually
    // after navigation to keep the same security parity.
    let iosWindow: Window | null = null;
    if (isIosWebKit()) {
      iosWindow = window.open("about:blank", "_blank");
      if (!iosWindow) {
        // Popup blocker refused even a synchronous open — rare, but
        // means the user has an explicit browser setting we can't
        // work around from JS.
        setDlError(t("result.pdf_error_popup_blocked"));
        setDlState("error");
        return;
      }
      // Immediate visual feedback in the newly opened tab. Otherwise
      // Safari sits on about:blank until the fetch resolves (which
      // can be 10-60s on cold render).
      try {
        iosWindow.document.write(
          '<!doctype html><meta charset="utf-8"><title>Yolutap</title>' +
            '<body style="margin:0;padding:40px;font:16px system-ui;color:#333">' +
            t("result.pdf_downloading") +
            "</body>",
        );
      } catch {
        // document.write can throw on cross-origin isolated contexts.
        // Not critical — the tab just stays blank while we fetch.
      }
    }

    setDlState("loading");
    setDlError(null);
    try {
      const { url } = await withTimeout(
        fetchPdfUrl({ data: { access_token: token } }),
        CLIENT_TIMEOUT_MS,
      );
      const childName = (data?.child_name ?? "report").replace(/[^\p{L}\p{N}_-]+/gu, "_");

      if (iosWindow) {
        // Navigate the pre-opened tab. The user gesture from the
        // original click still authorises this navigation on Safari.
        iosWindow.location.href = url;
        // Detach opener for security parity with what `noopener`
        // would have given us on the initial open. Can throw on
        // strict-origin isolation; ignore.
        try {
          iosWindow.opener = null;
        } catch {
          /* noop */
        }
      } else {
        triggerDesktopDownload(url, `yolutap-${childName}.pdf`);
      }
      setDlState("idle");
    } catch (e) {
      // Close the pre-opened iOS tab so the user isn't left staring
      // at our "Готовим файл…" placeholder on a dead render path.
      if (iosWindow) {
        try {
          iosWindow.close();
        } catch {
          /* noop */
        }
      }
      // Surface the error to the JS console so Safari Web Inspector
      // (Mac + iPhone) picks it up during debugging. Kept even when
      // we route the user through a friendly UI state — silent
      // console kills root-cause diagnosis for future iOS bugs.
      console.error("[pdf-download] handleDownload failed:", e);
      const msg = e instanceof Error ? e.message : String(e);
      // Map server-thrown Error names/messages + the client-side
      // timeout to UI copy keys. The server function throws typed
      // errors (SessionNotCompleteError, DownloadRateLimitError,
      // RenderFailedError, SessionNotFoundError); TanStack Start
      // server functions re-throw these as plain Errors on the
      // client with the original .message preserved.
      let key: string;
      if (/client timeout/i.test(msg)) key = "result.pdf_error_timeout";
      else if (/not complete|SessionNotComplete/i.test(msg)) key = "result.pdf_error_not_ready";
      else if (/rate limited|DownloadRateLimit|429/i.test(msg)) key = "result.pdf_error_429";
      else if (/renderReportPdf returned null|RenderFailed|env\.BROWSER/i.test(msg))
        key = "result.pdf_error_503";
      else key = "result.pdf_error_5xx";
      setDlError(t(key));
      setDlState("error");
    }
  }

  if (isLoading)
    return <div style={{ padding: 48, textAlign: "center" }}>{t("result.computing")}</div>;
  if (error)
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <p style={{ color: "#b00", marginBottom: 12 }}>
          {error instanceof Error ? error.message : t("common.error")}
        </p>
        <Link to="/session/$token" params={{ token }} style={{ color: "#06c" }}>
          {t("result.back_session")}
        </Link>
      </div>
    );
  if (!data) return <div style={{ padding: 48, textAlign: "center" }}>{t("common.no_data")}</div>;

  return (
    <div>
      <div
        className="print:hidden"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "#fff",
          borderBottom: "1px solid #e3e7eb",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        {dlError && (
          <span
            style={{
              fontSize: 12,
              color: "#b00",
              maxWidth: 460,
              textAlign: "right",
              lineHeight: 1.35,
            }}
          >
            {dlError}
          </span>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleDownload}
          disabled={dlState === "loading"}
        >
          {dlState === "loading" ? t("result.pdf_downloading") : t("result.download_pdf")}
        </Button>
      </div>
      {isAdult1822(data) ? (
        <Report13Page name={data.child_name} age={data.age} result={data.result1822} lang={lang} offers={data.offers} />
      ) : data.result.age_group === "16_18" ? (
        <Report12Page data={data} lang={lang} />
      ) : data.result.age_group === "14_15" ? (
        <Report11Page data={data} lang={lang} />
      ) : data.result.age_group === "10_11" ? (
        <Report1011Page data={data} lang={lang} />
      ) : (
        <Report10Page data={data} lang={lang} />
      )}
      {/* Телефон (часть D): блоки рекомендаций карточками под отчётом, только на узком экране. */}
      <ReportOffersMobile offers={data.offers?.byLang[lang]} lang={lang} />
      {/*
        Career Map entry — link to the public /careers hub, in the slot
        between the report and the consultation CTA (per ТЗ item 4b). Print
        hidden; locale follows the report language.
      */}
      {(() => {
        const cl = lang;
        const href = cl === "az" ? "/az/careers" : cl === "en" ? "/en/careers" : "/careers";
        return (
          <div className="print:hidden" style={{ textAlign: "center", margin: "10px 0 2px" }}>
            <a
              href={href}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#1E5A6B",
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
              }}
            >
              {CareersUI[cl].resultAllCareers}
            </a>
          </div>
        );
      })()}
      {/*
        Consultation CTA v2 — replaces the pre-ac79709 single-purpose
        BookingButton with a two-service picker (report_review /
        study_abroad). Rendered after the report and before the end
        of the page so families first read the results, then see the
        follow-up options. print:hidden inside the component itself.
      */}
      <ConsultationCTA
        accessToken={token}
        lang={lang}
        abroadOffer={(() => {
          // C7: та же персональная ссылка и те же тексты, что у блока final.
          const o = data.offers?.byLang[lang]?.final;
          if (!o || o.urlTemplate !== "{abroad_start}") return null;
          const r = isAdult1822(data) ? null : data.result;
          return {
            title: o.title,
            copy: o.text,
            cta: o.cta,
            href: offerHref(o, {
              lang,
              lead: (r?.primary_archetype as string | undefined) ?? null,
              supp: (r?.secondary_archetype as string | undefined) ?? null,
              medium: "web",
            }),
          };
        })()}
      />
    </div>
  );
}
